import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Facebook, Instagram, Linkedin, Youtube, ArrowRight, GraduationCap } from "lucide-react";

// Universities from our focus destinations
const universities = [
  // Finland
  { name: "University of Helsinki", country: "Finland" },
  { name: "Aalto University", country: "Finland" },
  { name: "Tampere University", country: "Finland" },
  { name: "University of Turku", country: "Finland" },
  { name: "LUT University", country: "Finland" },
  // Sweden
  { name: "Lund University", country: "Sweden" },
  { name: "Uppsala University", country: "Sweden" },
  { name: "KTH Royal Institute", country: "Sweden" },
  { name: "Stockholm University", country: "Sweden" },
  // UK
  { name: "University of Manchester", country: "UK" },
  { name: "University of Birmingham", country: "UK" },
  { name: "University of Leeds", country: "UK" },
  { name: "University of Glasgow", country: "UK" },
  // New Zealand
  { name: "University of Auckland", country: "New Zealand" },
  { name: "University of Otago", country: "New Zealand" },
  { name: "Victoria University", country: "New Zealand" },
  // Italy
  { name: "Politecnico di Milano", country: "Italy" },
  { name: "University of Bologna", country: "Italy" },
  { name: "Sapienza University", country: "Italy" },
];

const Footer = () => {
  const destinations = [
    { name: "Finland", slug: "finland" },
    { name: "Sweden", slug: "sweden" },
    { name: "United Kingdom", slug: "uk" },
    { name: "New Zealand", slug: "new-zealand" },
    { name: "Italy", slug: "italy" },
    { name: "Malta", slug: "malta" },
    { name: "Latvia", slug: "latvia" },
    { name: "South Korea", slug: "south-korea" },
    { name: "China", slug: "china" },
  ];

  const quickLinks = [
    { name: "About Us", path: "/about" },
    { name: "Our Services", path: "/services" },
    { name: "Testimonials", path: "/testimonials" },
    { name: "Resources", path: "/resources" },
    { name: "Contact Us", path: "/contact" },
  ];

  return (
    <footer className="bg-stone-900 text-white" data-testid="footer">
      {/* CTA Section */}
      <div className="bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-outfit text-2xl md:text-3xl font-bold text-white">
                Ready to Start Your Journey?
              </h3>
              <p className="text-white/80 mt-2">
                Get free consultation from our expert counselors today.
              </p>
            </div>
            <Link
              to="/contact"
              className="flex items-center gap-2 bg-white text-primary px-8 py-4 rounded-full font-bold hover:bg-stone-100 transition-colors"
              data-testid="footer-cta"
            >
              Contact Us Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Universities Marquee Section */}
      <div className="border-b border-stone-800 overflow-hidden">
        <div className="py-6">
          <div className="flex items-center justify-center gap-2 mb-4">
            <GraduationCap className="w-5 h-5 text-primary" />
            <h4 className="font-outfit text-sm font-semibold text-stone-400 uppercase tracking-wider">
              We Guide You to the Best Universities & Colleges in Europe & East Asia
            </h4>
          </div>
          
          {/* Scrolling Marquee */}
          <div className="relative">
            <div className="flex animate-marquee whitespace-nowrap">
              {[...universities, ...universities].map((uni, idx) => (
                <div
                  key={idx}
                  className="inline-flex items-center gap-2 mx-6 text-stone-400"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-primary"></span>
                  <span className="font-medium">{uni.name}</span>
                  <span className="text-stone-600 text-sm">({uni.country})</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <img
              src="/logo.png"
              alt="Sprungly Education"
              className="h-16 w-auto mb-6"
            />
            <p className="text-stone-400 mb-6">
              Your trusted partner for international education. Helping Sri Lankan students achieve their dreams of studying abroad since 2017.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center hover:bg-primary transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center hover:bg-primary transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center hover:bg-primary transition-colors"
                data-testid="social-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-stone-800 flex items-center justify-center hover:bg-primary transition-colors"
                data-testid="social-youtube"
              >
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Destinations */}
          <div>
            <h4 className="font-outfit text-lg font-bold mb-6">Study Destinations</h4>
            <ul className="space-y-3">
              {destinations.map((dest) => (
                <li key={dest.slug}>
                  <Link
                    to={`/destinations/${dest.slug}`}
                    className="text-stone-400 hover:text-primary transition-colors"
                    data-testid={`footer-dest-${dest.slug}`}
                  >
                    {dest.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-outfit text-lg font-bold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-stone-400 hover:text-primary transition-colors"
                    data-testid={`footer-link-${link.path.replace("/", "")}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-outfit text-lg font-bold mb-6">Contact Us</h4>
            <div className="space-y-4">
              <a 
                href="https://maps.app.goo.gl/AwQADyuzv2h4wLiN9"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 group"
              >
                <MapPin className="w-5 h-5 text-primary mt-1" />
                <p className="text-stone-400 group-hover:text-primary transition-colors">
                  Sprungly Education (Pvt) Ltd,<br />
                  6 M-2, IBC Road, Colombo 06
                </p>
              </a>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-primary" />
                <div>
                  <a
                    href="tel:+94112555500"
                    className="text-stone-400 hover:text-primary transition-colors block"
                    data-testid="footer-phone"
                  >
                    +94 011 255 5500
                  </a>
                  <a
                    href="tel:+358454907613"
                    className="text-stone-400 hover:text-primary transition-colors block"
                  >
                    +358 45 490 7613
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-primary" />
                <a
                  href="mailto:manager@sprungly.com"
                  className="text-stone-400 hover:text-primary transition-colors"
                  data-testid="footer-email"
                >
                  manager@sprungly.com
                </a>
              </div>
              
              {/* Google Maps Embed */}
              <div className="mt-4 rounded-xl overflow-hidden border border-stone-700">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3960.9028893459!2d79.85463691476908!3d6.897202995003!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae25bcf7b123f63%3A0x5c8e4b8e8f8e4b8e!2s6%20M-2%2C%20IBC%20Road%2C%20Colombo%2006!5e0!3m2!1sen!2slk!4v1712300000000!5m2!1sen!2slk"
                  width="100%"
                  height="150"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sprungly Education Location"
                  className="grayscale hover:grayscale-0 transition-all"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-stone-500 text-sm">
              © {new Date().getFullYear()} Sprungly Education. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-stone-500 text-sm hover:text-primary transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-stone-500 text-sm hover:text-primary transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
